import Box from '@mui/material/Box';
import { default as Checkbox, CheckboxProps } from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { Field, NextImage, RichText, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { IItemWithString, ISitecoreImage } from 'lib/type/common';
import Image from 'next/image';
import React, { forwardRef, useEffect, useState } from 'react';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { updateRegistration } from 'src/features/registrationSlice';
import { RootState } from 'src/store/store';
import EmailAutoSugest from 'src/components/shared/EmailAutocomplete';
import notificationIcon from './notification_icon.svg';
import StRegistrationContainer, { StRegistrationWrapper } from './Registration.style';
import useScrollToElement from '../../../lib/hooks/useScrollToElement';
import axios from 'axios';
import { useI18n } from 'next-localization';
import { useRouter } from 'next/router';
import * as cdpService from '../../../services/CdpService';
import { getCookie } from 'lib/utils/enrollmentUtil';
import { store } from 'src/store/store';
import { updateLastVisitedComponentId } from '../../../features/globalConfig';
import { hideLoader, showLoader } from '../../../features/loaderSlice';
import { resetCookies } from '../../../lib/utils/cookieHelper';
import UiHeading from '../../shared/Heading';
import UiBox from '../../shared/Box';
import ButtonSecondary from '../../shared/Button/SecondaryButton';
import { getValidateCrEmailIdURL } from '../../../lib/utils/common';
import { regExpFromSitecorePattern } from '../../../lib/utils/contactForm';
import { getContactEmailDomainsListForContactInfo } from 'src/GraphQL/queries/contactEmailDomainsList.query';

type CheckboxInputProps = ControllerRenderProps & CheckboxProps & any;

interface RegistrationProps {
  fields: {
    consentDescription: Field<string>;
    consentRequiredValidationMessage: Field<string>;
    content: Field<string>;
    ctaLink: any;
    emailDomains: IItemWithString[];
    emailRegex: any;
    emailTitle: Field<string>;
    heading: Field<string>;
    invalidInputEmailValidationMessage: Field<string>;
    isConsentRequired: Field<boolean>;
    isEmailRequired: Field<boolean>;
    requiredEmailValidationMessage: Field<string>;
    sectionIcon: ISitecoreImage;
    emailExistsMessage: Field<string>;
  };
}
const Registartion: React.FC<RegistrationProps> = (props: RegistrationProps) => {
  const {
    control,
    formState: { errors, isValid },
    getValues,
    setError,
    handleSubmit,
  } = useForm({ mode: 'onBlur' });
  const dispatch = useDispatch();
  const registration = useSelector((state: RootState) => state.registrationDetails.registration);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(500));
  const { t } = useI18n();
  const router = useRouter();
  const scrollId = useSelector((state: RootState) => state.scrollId.scrollId);
  const scrollToElement = useScrollToElement();
  const emailExistsMessage = props?.fields?.emailExistsMessage;
  const { sitecoreContext } = useSitecoreContext();
  const { siteSettings }: any = sitecoreContext;
  const [emailDomains, setEmailDomains] = useState<IItemWithString[]>([]);
  const state = store.getState();
  const isAuthenticated: boolean = state.authentication.isAuthenticated;
  const { loadType } = router.query;

  const language = sitecoreContext?.language || process.env.DEFAULT_LANGUAGE || 'en';

  useEffect(() => {
    void (async () => {
      try {
        if (emailDomains?.length > 0) {
          return;
        }
        const list = await getContactEmailDomainsListForContactInfo(language, siteSettings);

        setEmailDomains(list);
      } catch {}
    })();
  }, [siteSettings]);
  const config = {
    clientKey: process.env.NEXT_PUBLIC_CDP_CLIENT_KEY || 'de665c8ff4f47bfd6e5f7313baff0442',
    targetURL:
      process.env.NEXT_PUBLIC_CDP_API_TARGET_ENDPOINT || 'https://api-engage-eu.sitecorecloud.io',
    pointOfSale: sitecoreContext?.site?.name,
    cookieDomain: '',
    cookieExpiryDays: 365,
    forceServerCookieMode: false,
    includeUTMParameters: true,
    webPersonalization: true,
    scriptUrl:
      process.env.NEXT_PUBLIC_SITECORE_ENGAGE ||
      'https://d1mj578wat5n4o.cloudfront.net/sitecore-engage-v.1.3.0.min.js',
  };

  const loadCDPScript = (data: any) => {
    // Remove existing script if present
    const existingCdpScript: HTMLElement | null = document.getElementById(
      'cdp-script'
    ) as HTMLElement | null;
    if (existingCdpScript) {
      document.head.removeChild(existingCdpScript);
    }

    // Only load script after user interaction (e.g., registration button click)
    const script = document.createElement('script');
    script.src = config.scriptUrl; // The URL of the Sitecore Engage script
    script.async = true;
    script.id = 'cdp-script';
    resetCookies();

    document.head.appendChild(script);
    script.onload = async function () {
      if (window.Engage && typeof window.Engage.init === 'function') {
        const engage = await window.Engage.init(config);
        window.Engage.exposedFunctions = engage;
        cdpService.identityEvent(data?.email);
        dispatch(updateRegistration(getValues()));
        router.push(props?.fields?.ctaLink?.value?.href);
      }
    };
  };

  useEffect(() => {
    if (scrollId) {
      scrollToElement(scrollId);
    }
  }, [scrollId]);

  const handleRegistration = async (data: any) => {
    try {
      const URL = getValidateCrEmailIdURL(
        data?.email,
        getCookie('bx_guest_ref'),
        siteSettings?.source?.fields?.name?.value || 'CR',
        false
      );
      dispatch(showLoader());
      const response = await axios.get(URL);
      if (response?.data?.isEmailExist) {
        setError('email', { type: 'manual', message: emailExistsMessage?.value });
      } else if (response?.data?.isReloadCdpScript) {
        loadCDPScript(data);
      } else {
        cdpService.identityEvent(data?.email);
        dispatch(updateRegistration(getValues()));
        dispatch(updateLastVisitedComponentId('cr-registration'));
        router.push(props?.fields?.ctaLink?.value?.href);
      }
      dispatch(hideLoader());
    } catch (_error) {
      dispatch(hideLoader());
    }
  };

  const CheckboxInput: React.FC<CheckboxInputProps> = forwardRef(
    ({ value, ...rest }, ref: CheckboxInputProps) => {
      return (
        <Checkbox
          checked={!!value}
          {...rest}
          sx={{
            alignSelf: 'flex-start',
            padding: '0 9px',
            '.MuiCheckbox-root': {
              color: 'black',
              backgroundColor: 'black',
            },
          }}
          id="consent-checkbox"
          className="checkbox"
          value="remember"
          color="primary"
          ref={ref}
        />
      );
    }
  );
  CheckboxInput.displayName = 'CheckboxInput';

  if (!props?.fields || isAuthenticated || loadType === 'iframe' || !router.isReady) return <></>;
  return (
    <StRegistrationContainer id="cr-registration" className="ui-cr-registration">
      <form onSubmit={handleSubmit(handleRegistration)}>
        <StRegistrationWrapper id="cr-registration-wrapper" className="ui-cr-registration__wrapper">
          <Box alignSelf="center">
            <NextImage field={props?.fields?.sectionIcon} height={89} width={89} />
          </Box>
          <Box>
            <UiBox styles={{ marginBottom: '1rem' }}>
              <UiHeading size="h2" color="white" align="center" id="ui-registration-heading">
                {props?.fields?.heading}
              </UiHeading>
            </UiBox>
            <Box
              style={{
                lineHeight: '1.875rem',
                color: theme.palette.white.main,
                fontSize: '20px',
                fontWeight: 400,
                textAlign: 'center',
              }}
            >
              <RichText field={props?.fields?.content} />
            </Box>
          </Box>
          {errors && (errors.email || errors.agree) && (
            <Box
              sx={{
                width: isMobile ? '100%' : '420px',
                alignSelf: 'center',
              }}
            >
              <Paper sx={{ p: 2, borderRadius: '8px', backgroundColor: '#E6F9F8' }}>
                <Stack direction="row" alignItems="center" sx={{ marginLeft: '0.5rem' }}>
                  <Image
                    style={{ color: theme.palette.error.main }}
                    priority
                    src={notificationIcon}
                    alt="Notification icon"
                  />
                  <Typography
                    sx={{
                      color: theme.palette.error.main,
                      fontWeight: 400,
                      lineHeight: '1.25rem',
                      marginLeft: '1rem',
                    }}
                  >
                    {`${errors?.email ? errors?.email?.message : errors.agree?.message}`}
                  </Typography>
                </Stack>
              </Paper>
            </Box>
          )}
          <Box
            sx={{
              width: isMobile ? '100%' : '420px',
              alignSelf: 'center',
              marginTop: errors && (errors.email || errors.agree) ? '0' : '2rem',
            }}
          >
            <Controller
              control={control}
              name="email"
              defaultValue={registration.email}
              rules={{
                required: {
                  value: props?.fields?.isEmailRequired?.value || false,
                  message: props?.fields?.requiredEmailValidationMessage?.value || '',
                },
                validate: async (value) => {
                  const regEx = regExpFromSitecorePattern(
                    props?.fields?.emailRegex?.fields?.value?.value
                  );
                  return regEx.test(value)
                    ? true
                    : props.fields.invalidInputEmailValidationMessage.value ?? '';
                },
              }}
              render={({ field }) => (
                <EmailAutoSugest
                  emailDomains={emailDomains}
                  emailTitle={props?.fields?.emailTitle}
                  fieldProps={field}
                />
              )}
            />
          </Box>
          <Box sx={{ width: isMobile ? '100%' : '420px', alignSelf: 'center', paddingTop: '8px' }}>
            <Controller
              control={control}
              name="agree"
              defaultValue={registration.agree}
              rules={{
                required: {
                  value: props?.fields?.isConsentRequired?.value || false,
                  message: props?.fields?.consentRequiredValidationMessage?.value || '',
                },
              }}
              render={({ field }) => (
                <FormControl>
                  <FormControlLabel
                    className="checkboxLabel"
                    control={<CheckboxInput {...field} />}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        color: 'inherit',
                      },
                    }}
                    label={
                      <RichText
                        className="description"
                        id="ui-registeration-consent-checkbox-label"
                        field={props?.fields?.consentDescription}
                      />
                    }
                  />
                </FormControl>
              )}
            />
          </Box>
          <UiBox
            styles={{
              alignSelf: 'center',
              padding: '24px 0 10px 0',
              minWidth: '205px',
            }}
            id="getstarted"
          >
            <ButtonSecondary fullWidth disabled={!isValid} id="get-started" type="submit">
              {t(props?.fields?.ctaLink?.value?.text)
                ? t(props?.fields?.ctaLink?.value?.text)
                : 'Get started'}
            </ButtonSecondary>
          </UiBox>
        </StRegistrationWrapper>
      </form>
    </StRegistrationContainer>
  );
};

export default Registartion;
